import { useMemo } from 'react';

export interface FilterSelections {
  channels: string[];
  categories: string[];
  genders: string[];
  collections: string[];
  colors: string[];
  styles: string[];
}

export interface PerformanceItem {
  [key: string]: string | number;
  shopify_revenue: number;
  wholesale_revenue: number;
  total_revenue: number;
}

export interface CumulativePerformance {
  category_performance: Array<PerformanceItem & { category: string }>;
  gender_performance: Array<PerformanceItem & { gender: string }>;
  collection_performance: Array<PerformanceItem & { collection: string }>;
  color_performance: Array<PerformanceItem & { color: string }>;
  style_performance: Array<PerformanceItem & { style: string }>;
}

export interface FilteredMetrics {
  totalRevenue: number;
  shopifyRevenue: number;
  wholesaleRevenue: number;
  itemCount: number;
  hasActiveFilters: boolean;
  matchingItems: Array<{
    dimension: string;
    name: string;
    shopify_revenue: number;
    wholesale_revenue: number;
    total_revenue: number;
  }>;
}

/**
 * Hook to filter cumulative performance data based on selected dimensions
 * Returns aggregated metrics for the filtered view
 */
export function useFilteredPerformance(
  cumulativePerformance: CumulativePerformance | null | undefined,
  filters: FilterSelections
): FilteredMetrics {
  return useMemo(() => {
    if (!cumulativePerformance) {
      return {
        totalRevenue: 0,
        shopifyRevenue: 0,
        wholesaleRevenue: 0,
        itemCount: 0,
        hasActiveFilters: false,
        matchingItems: [],
      };
    }

    const hasActiveFilters =
      filters.channels.length > 0 ||
      filters.categories.length > 0 ||
      filters.genders.length > 0 ||
      filters.collections.length > 0 ||
      filters.colors.length > 0 ||
      filters.styles.length > 0;

    // If no filters are active, return totals from all data
    if (!hasActiveFilters) {
      const allItems: Array<{
        dimension: string;
        name: string;
        shopify_revenue: number;
        wholesale_revenue: number;
        total_revenue: number;
      }> = [];

      const categoryItems = cumulativePerformance.category_performance.map(item => ({
        dimension: 'Category',
        name: item.category || 'Unknown',
        shopify_revenue: item.shopify_revenue || 0,
        wholesale_revenue: item.wholesale_revenue || 0,
        total_revenue: item.total_revenue || 0,
      }));

      allItems.push(...categoryItems);

      const totalShopify = allItems.reduce((sum, item) => sum + item.shopify_revenue, 0);
      const totalWholesale = allItems.reduce((sum, item) => sum + item.wholesale_revenue, 0);

      return {
        totalRevenue: totalShopify + totalWholesale,
        shopifyRevenue: totalShopify,
        wholesaleRevenue: totalWholesale,
        itemCount: 0,
        hasActiveFilters: false,
        matchingItems: [],
      };
    }

    // Filter and aggregate based on selections
    const matchingItems: Array<{
      dimension: string;
      name: string;
      shopify_revenue: number;
      wholesale_revenue: number;
      total_revenue: number;
    }> = [];

    // Apply channel filter (Shopify vs Wholesale)
    const filterByChannel = (item: PerformanceItem) => {
      if (filters.channels.length === 0) return true;
      
      const hasShopify = filters.channels.includes('Shopify');
      const hasWholesale = filters.channels.includes('Wholesale');
      
      if (hasShopify && hasWholesale) return true;
      if (hasShopify && item.shopify_revenue > 0) return true;
      if (hasWholesale && item.wholesale_revenue > 0) return true;
      
      return false;
    };

    // Filter categories
    if (filters.categories.length > 0) {
      cumulativePerformance.category_performance.forEach((item) => {
        if (filters.categories.includes(item.category) && filterByChannel(item)) {
          let shopifyRev = item.shopify_revenue || 0;
          let wholesaleRev = item.wholesale_revenue || 0;

          // Apply channel-specific filtering
          if (filters.channels.length > 0) {
            if (!filters.channels.includes('Shopify')) shopifyRev = 0;
            if (!filters.channels.includes('Wholesale')) wholesaleRev = 0;
          }

          matchingItems.push({
            dimension: 'Category',
            name: item.category || 'Unknown',
            shopify_revenue: shopifyRev,
            wholesale_revenue: wholesaleRev,
            total_revenue: shopifyRev + wholesaleRev,
          });
        }
      });
    }

    // Filter genders
    if (filters.genders.length > 0) {
      cumulativePerformance.gender_performance.forEach((item) => {
        if (filters.genders.includes(item.gender) && filterByChannel(item)) {
          let shopifyRev = item.shopify_revenue || 0;
          let wholesaleRev = item.wholesale_revenue || 0;

          if (filters.channels.length > 0) {
            if (!filters.channels.includes('Shopify')) shopifyRev = 0;
            if (!filters.channels.includes('Wholesale')) wholesaleRev = 0;
          }

          matchingItems.push({
            dimension: 'Gender',
            name: item.gender || 'Unknown',
            shopify_revenue: shopifyRev,
            wholesale_revenue: wholesaleRev,
            total_revenue: shopifyRev + wholesaleRev,
          });
        }
      });
    }

    // Filter collections
    if (filters.collections.length > 0) {
      cumulativePerformance.collection_performance.forEach((item) => {
        if (filters.collections.includes(item.collection) && filterByChannel(item)) {
          let shopifyRev = item.shopify_revenue || 0;
          let wholesaleRev = item.wholesale_revenue || 0;

          if (filters.channels.length > 0) {
            if (!filters.channels.includes('Shopify')) shopifyRev = 0;
            if (!filters.channels.includes('Wholesale')) wholesaleRev = 0;
          }

          matchingItems.push({
            dimension: 'Collection',
            name: item.collection || 'Unknown',
            shopify_revenue: shopifyRev,
            wholesale_revenue: wholesaleRev,
            total_revenue: shopifyRev + wholesaleRev,
          });
        }
      });
    }

    // Filter colors
    if (filters.colors.length > 0) {
      cumulativePerformance.color_performance.forEach((item) => {
        if (filters.colors.includes(item.color) && filterByChannel(item)) {
          let shopifyRev = item.shopify_revenue || 0;
          let wholesaleRev = item.wholesale_revenue || 0;

          if (filters.channels.length > 0) {
            if (!filters.channels.includes('Shopify')) shopifyRev = 0;
            if (!filters.channels.includes('Wholesale')) wholesaleRev = 0;
          }

          matchingItems.push({
            dimension: 'Color',
            name: item.color || 'Unknown',
            shopify_revenue: shopifyRev,
            wholesale_revenue: wholesaleRev,
            total_revenue: shopifyRev + wholesaleRev,
          });
        }
      });
    }

    // Filter styles
    if (filters.styles.length > 0) {
      cumulativePerformance.style_performance.forEach((item) => {
        if (filters.styles.includes(item.style) && filterByChannel(item)) {
          let shopifyRev = item.shopify_revenue || 0;
          let wholesaleRev = item.wholesale_revenue || 0;

          if (filters.channels.length > 0) {
            if (!filters.channels.includes('Shopify')) shopifyRev = 0;
            if (!filters.channels.includes('Wholesale')) wholesaleRev = 0;
          }

          matchingItems.push({
            dimension: 'Style',
            name: item.style || 'Unknown',
            shopify_revenue: shopifyRev,
            wholesale_revenue: wholesaleRev,
            total_revenue: shopifyRev + wholesaleRev,
          });
        }
      });
    }

    // If only channel filter is applied, include all dimensions
    if (filters.channels.length > 0 && 
        filters.categories.length === 0 && 
        filters.genders.length === 0 && 
        filters.collections.length === 0 && 
        filters.colors.length === 0 && 
        filters.styles.length === 0) {
      
      // Add all categories with channel filter
      cumulativePerformance.category_performance.forEach((item) => {
        let shopifyRev = filters.channels.includes('Shopify') ? (item.shopify_revenue || 0) : 0;
        let wholesaleRev = filters.channels.includes('Wholesale') ? (item.wholesale_revenue || 0) : 0;

        if (shopifyRev > 0 || wholesaleRev > 0) {
          matchingItems.push({
            dimension: 'Category',
            name: item.category || 'Unknown',
            shopify_revenue: shopifyRev,
            wholesale_revenue: wholesaleRev,
            total_revenue: shopifyRev + wholesaleRev,
          });
        }
      });
    }

    // Calculate totals from matching items
    const totalShopify = matchingItems.reduce((sum, item) => sum + item.shopify_revenue, 0);
    const totalWholesale = matchingItems.reduce((sum, item) => sum + item.wholesale_revenue, 0);

    return {
      totalRevenue: totalShopify + totalWholesale,
      shopifyRevenue: totalShopify,
      wholesaleRevenue: totalWholesale,
      itemCount: matchingItems.length,
      hasActiveFilters: true,
      matchingItems: matchingItems.sort((a, b) => b.total_revenue - a.total_revenue),
    };
  }, [cumulativePerformance, filters]);
}


